import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, AlertTriangle, Loader2 } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const SkillRadarChart = ({ roadmaps = [], analytics = null }) => {
    const [selectedRoadmap, setSelectedRoadmap] = useState(null);
    const [animationProgress, setAnimationProgress] = useState(0);
    const [apiSkills, setApiSkills] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (roadmaps.length > 0 && !selectedRoadmap) {
            setSelectedRoadmap(roadmaps[0]);
        }
    }, [roadmaps]);

    // Fetch skill data from backend
    useEffect(() => {
        const fetchSkills = async () => {
            setLoading(true);
            setError(false);
            try {
                const result = await featuresAPI.getSkillRadar(selectedRoadmap?.id || null);
                if (result?.skills && result.skills.length > 0) {
                    setApiSkills(result);
                } else {
                    setError(true);
                }
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchSkills();
    }, [selectedRoadmap]);

    useEffect(() => {
        setAnimationProgress(0);
        const timer = setTimeout(() => setAnimationProgress(1), 100);
        return () => clearTimeout(timer);
    }, [selectedRoadmap, apiSkills]);

    const skillData = useMemo(() => {
        if (!apiSkills?.skills) return [];
        return apiSkills.skills;
    }, [apiSkills]);

    const size = 280;
    const center = size / 2;
    const maxRadius = size / 2 - 40;
    const levels = 5;

    const getPoint = (index, value, total) => {
        const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
        const radius = (value / 100) * maxRadius * animationProgress;
        return {
            x: center + radius * Math.cos(angle),
            y: center + radius * Math.sin(angle),
        };
    };

    const getPolygonPoints = (values) => {
        return values.map((v, i) => {
            const point = getPoint(i, v, values.length);
            return `${point.x},${point.y}`;
        }).join(' ');
    };

    const avgGap = useMemo(() => {
        if (apiSkills?.avg_gap != null) return apiSkills.avg_gap;
        if (!skillData.length) return 0;
        const gaps = skillData.map(s => s.required - s.current);
        return Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length);
    }, [skillData, apiSkills]);

    const overallReadiness = useMemo(() => {
        if (apiSkills?.overall_readiness != null) return apiSkills.overall_readiness;
        if (!skillData.length) return 0;
        return Math.round(skillData.reduce((a, s) => a + (s.current / s.required) * 100, 0) / skillData.length);
    }, [skillData, apiSkills]);

    if (roadmaps.length === 0) return null;

    // Loading state
    if (loading) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Target className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-bold text-white text-sm">Skill Radar</h3>
                </div>
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                    <span className="ml-2 text-xs text-slate-500">Analyzing your skills...</span>
                </div>
            </div>
        );
    }

    // Error / empty state
    if (error || !skillData.length) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Target className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-bold text-white text-sm">Skill Radar</h3>
                </div>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertTriangle className="w-8 h-8 text-slate-600 mb-3" />
                    <p className="text-sm text-slate-400 mb-1">Skill data unavailable</p>
                    <p className="text-xs text-slate-600">Complete tasks in your roadmap to build your skill profile</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-bold text-white text-sm">Skill Radar</h3>
                </div>
                {roadmaps.length > 1 && (
                    <select
                        value={selectedRoadmap?.id || ''}
                        onChange={(e) => setSelectedRoadmap(roadmaps.find(r => r.id === e.target.value))}
                        className="text-xs bg-[#0B0F19] border border-white/10 text-slate-400 px-2 py-1 rounded-lg focus:outline-none"
                    >
                        {roadmaps.map(r => (
                            <option key={r.id} value={r.id}>{r.careerPath}</option>
                        ))}
                    </select>
                )}
            </div>

            <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* SVG Radar */}
                <div className="relative">
                    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                        {/* Grid lines */}
                        {Array.from({ length: levels }, (_, i) => {
                            const radius = (maxRadius * (i + 1)) / levels;
                            const points = skillData.map((_, j) => {
                                const angle = (Math.PI * 2 * j) / skillData.length - Math.PI / 2;
                                return `${center + radius * Math.cos(angle)},${center + radius * Math.sin(angle)}`;
                            }).join(' ');
                            return (
                                <polygon
                                    key={i}
                                    points={points}
                                    fill="none"
                                    stroke="rgba(255,255,255,0.05)"
                                    strokeWidth="1"
                                />
                            );
                        })}

                        {/* Axis lines */}
                        {skillData.map((_, i) => {
                            const angle = (Math.PI * 2 * i) / skillData.length - Math.PI / 2;
                            return (
                                <line
                                    key={i}
                                    x1={center}
                                    y1={center}
                                    x2={center + maxRadius * Math.cos(angle)}
                                    y2={center + maxRadius * Math.sin(angle)}
                                    stroke="rgba(255,255,255,0.05)"
                                    strokeWidth="1"
                                />
                            );
                        })}

                        {/* Required polygon */}
                        <motion.polygon
                            points={getPolygonPoints(skillData.map(s => s.required))}
                            fill="rgba(239, 68, 68, 0.05)"
                            stroke="rgba(239, 68, 68, 0.3)"
                            strokeWidth="1.5"
                            strokeDasharray="4 4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        />

                        {/* Current polygon */}
                        <motion.polygon
                            points={getPolygonPoints(skillData.map(s => s.current))}
                            fill="rgba(16, 185, 129, 0.1)"
                            stroke="rgba(16, 185, 129, 0.6)"
                            strokeWidth="2"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        />

                        {/* Data points */}
                        {skillData.map((s, i) => {
                            const point = getPoint(i, s.current, skillData.length);
                            return (
                                <motion.circle
                                    key={i}
                                    cx={point.x}
                                    cy={point.y}
                                    r="4"
                                    fill="#10b981"
                                    stroke="#0B0F19"
                                    strokeWidth="2"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.5 + i * 0.1 }}
                                />
                            );
                        })}

                        {/* Labels */}
                        {skillData.map((s, i) => {
                            const angle = (Math.PI * 2 * i) / skillData.length - Math.PI / 2;
                            const labelRadius = maxRadius + 25;
                            const x = center + labelRadius * Math.cos(angle);
                            const y = center + labelRadius * Math.sin(angle);
                            return (
                                <text
                                    key={i}
                                    x={x}
                                    y={y}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill="#94a3b8"
                                    fontSize="9"
                                    fontFamily="monospace"
                                >
                                    {s.skill}
                                </text>
                            );
                        })}
                    </svg>
                </div>

                {/* Legend & Stats */}
                <div className="flex-1 space-y-4 w-full">
                    <div className="flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-2">
                            <span className="w-3 h-1 bg-emerald-500 rounded"></span>
                            <span className="text-slate-400">Your Level</span>
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-3 h-1 bg-red-500/50 rounded border border-red-500/30" style={{ borderStyle: 'dashed' }}></span>
                            <span className="text-slate-400">Target</span>
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-3 text-center">
                            <div className="text-2xl font-bold text-emerald-400">{overallReadiness}%</div>
                            <div className="text-[10px] text-slate-500 font-mono uppercase">Readiness</div>
                        </div>
                        <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-3 text-center">
                            <div className="text-2xl font-bold text-amber-400">{avgGap}%</div>
                            <div className="text-[10px] text-slate-500 font-mono uppercase">Avg Gap</div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        {skillData.map((s, i) => (
                            <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-slate-400 w-24 truncate">{s.skill}</span>
                                <div className="flex-1 mx-2 h-1 bg-slate-800 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-1000"
                                        style={{
                                            width: `${(s.current / s.required) * 100}%`,
                                            background: s.current >= s.required * 0.8 ? '#10b981' : s.current >= s.required * 0.5 ? '#f59e0b' : '#ef4444'
                                        }}
                                    />
                                </div>
                                <span className="text-slate-500 font-mono w-12 text-right">{Math.round(s.current)}/{s.required}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkillRadarChart;
